import type { IServerSideSelectionState } from "ag-grid-enterprise";
import type { TFunction } from "i18next";
import type { ContinuationTarget } from "~/helpers/pendingIntentLifecycle";
import type { LaborScheduleSsrmRow } from "~produce/labor/schedule/helpers/laborScheduleSsrmRow";
import type { ReceiverIdentityAdapter } from "~produce/production/helpers/continuationAdapter";

export const LABOR_SCHEDULE_SOURCE_KIND = "laborSchedule";

export const LABOR_WORKER_TARGET_KIND = "laborWorker";

export const LABOR_SCHEDULE_BASE_PATH = "/produce/labor/schedule";

export const LABOR_SCHEDULE_DOCK_PANEL_ID = "labor-schedule";

const LABOR_WORKER_ROUTE_PATTERN = /^\/produce\/labor\/schedule\/workers\/([^/?#]+)$/;

export interface LaborScheduleAssignDialogCheckpoint {
	fromGridSelection: boolean;
	rowIds: string[];
}

export interface LaborScheduleWorkerPanelCheckpoint {
	selectedWorkerId: null | string;
}

export interface LaborScheduleSourceCheckpoint {
	assignDialog: LaborScheduleAssignDialogCheckpoint | null;
	blockLabel: string;
	detailRowIds: null | string[];
	gridSelection: IServerSideSelectionState | null;
	searchText: string;
	workerPanel: LaborScheduleWorkerPanelCheckpoint | null;
}

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null;

const isStringArray = (value: unknown): value is string[] =>
	Array.isArray(value) && value.every((item) => typeof item === "string");

const isAssignDialogCheckpoint = (value: unknown): value is LaborScheduleAssignDialogCheckpoint =>
	isRecord(value) && typeof value.fromGridSelection === "boolean" && isStringArray(value.rowIds);

const isWorkerPanelCheckpoint = (value: unknown): value is LaborScheduleWorkerPanelCheckpoint =>
	isRecord(value) && (value.selectedWorkerId === null || typeof value.selectedWorkerId === "string");

const isGridSelection = (value: unknown): value is IServerSideSelectionState =>
	isRecord(value) && typeof value.selectAll === "boolean" && isStringArray(value.toggledNodes);

export const isLaborScheduleSourceCheckpoint = (value: unknown): value is LaborScheduleSourceCheckpoint =>
	isRecord(value) &&
	(value.assignDialog === null || isAssignDialogCheckpoint(value.assignDialog)) &&
	typeof value.blockLabel === "string" &&
	(value.detailRowIds === null || isStringArray(value.detailRowIds)) &&
	(value.gridSelection === null || isGridSelection(value.gridSelection)) &&
	typeof value.searchText === "string" &&
	(value.workerPanel === null || isWorkerPanelCheckpoint(value.workerPanel));

export const formatLaborBlockLabel = (
	t: TFunction<"produce">,
	row: Pick<LaborScheduleSsrmRow, "blockName" | "taskEventTypeName">
): string => t("labor.schedule.blockLabel", { block: row.blockName, task: row.taskEventTypeName });

export const collectLaborScheduleRowIds = (rows: readonly LaborScheduleSsrmRow[]): string[] =>
	rows.flatMap((row) => row.rowId ?? []);

export const pruneGridSelection = (
	selection: IServerSideSelectionState,
	freshRowIds: ReadonlySet<string>
): IServerSideSelectionState =>
	selection.selectAll
		? selection
		: { selectAll: false, toggledNodes: selection.toggledNodes.filter((rowId) => freshRowIds.has(rowId)) };

export const matchLaborWorkerReceiverRoute = (pathname: string): ContinuationTarget | null => {
	const match = LABOR_WORKER_ROUTE_PATTERN.exec(pathname);
	if (!match) return null;
	try {
		return { targetId: decodeURIComponent(match[1]), targetKind: LABOR_WORKER_TARGET_KIND };
	} catch {
		return null;
	}
};

export const laborWorkerReceiverIdentityAdapter: ReceiverIdentityAdapter = {
	buildCleanPath: ({ targetId }) => `${LABOR_SCHEDULE_BASE_PATH}/workers/${encodeURIComponent(targetId)}`,
	matchesRoute: matchLaborWorkerReceiverRoute
};

const buildLaborScheduleEditorKey = (checkpoint: LaborScheduleSourceCheckpoint): string =>
	`${LABOR_SCHEDULE_DOCK_PANEL_ID}:${(checkpoint.assignDialog?.rowIds ?? checkpoint.detailRowIds ?? []).join(",")}`;

export default buildLaborScheduleEditorKey;
